/**
 * Portfolio View — cross-client dashboard applying the same left-rail filters
 * as the Asset Register. Renewal / Condition / Top Sites / Top Types breakdowns.
 * Companion to AssetRegister — reuses the localStorage filter state so a
 * user's picks persist across tab switches.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell } from "recharts";
import { AlertTriangle, TrendingDown, DollarSign, Package } from "lucide-react";

const fmt$ = (v) => new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 }).format(v || 0);
const fmt$k = (v) => v >= 1000000 ? `$${(v/1000000).toFixed(1)}M` : v >= 1000 ? `$${(v/1000).toFixed(0)}K` : `$${(v||0).toFixed(0)}`;
const fmtN = (v) => new Intl.NumberFormat("en-AU").format(v || 0);
const HORIZON_COLORS = { immediate: "#dc2626", "12m": "#f59e0b", "24m": "#3b82f6", "5y": "#10b981", "10y": "#6366f1", deferred: "#94a3b8" };
const HORIZON_LABEL  = { immediate: "Immediate", "12m": "≤12M", "24m": "13-24M", "5y": "3-5Y", "10y": "6-10Y", deferred: ">10Y" };
const COND_COLORS = { 1: "#10b981", 2: "#84cc16", 3: "#f59e0b", 4: "#ef4444", 5: "#7f1d1d" };


export default function AssetPortfolio() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const LS_KEY = "gbe_asset_register_filters_v1";

  const readFilters = () => {
    try { return JSON.parse(localStorage.getItem(LS_KEY) || "{}"); } catch { return {}; }
  };
  const load = async () => {
    setBusy(true);
    try {
      const f = readFilters();
      const params = new URLSearchParams({
        clients: (f.clients || []).join(","),
        sites: (f.sites || []).join(","),
        asset_types: (f.types || []).join(","),
        condition_min: f.condMin ?? 0,
        condition_max: f.condMax ?? 5,
      });
      const { data } = await api.get(`/capital/asset-register/portfolio?${params}`);
      setData(data);
    } catch (e) {
      toast.error("Failed to load Portfolio View");
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);
  // Reload on filter changes — react to both cross-tab `storage` events and
  // same-window `gbe:capital-filters-changed` custom events (top-right client
  // selector, Asset Register sidebar, etc.).
  useEffect(() => {
    const onStorage = (e) => { if (e.key === LS_KEY) load(); };
    const onSameWindow = () => load();
    window.addEventListener("storage", onStorage);
    window.addEventListener("gbe:capital-filters-changed", onSameWindow);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("gbe:capital-filters-changed", onSameWindow);
    };
  }, []);

  if (!data) return <div className="h-64 bg-white border border-slate-200 rounded-lg shimmer" />;
  const k = data.kpis || {};

  return (
    <div className="space-y-4">
      <div className="text-[11px] text-slate-500">
        Filters applied via the <b>Asset Register</b> tab persist here. Charts + tables re-compute against your filter selection.
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <Tile icon={Package}       label="Assets"          value={fmtN(k.asset_count)} tone="default" />
        <Tile icon={DollarSign}    label="Replacement $"   value={fmt$(k.total_cost)}  tone="info" />
        <Tile icon={TrendingDown}  label="Avg Condition"   value={k.avg_condition ?? "—"} tone="default" />
        <Tile icon={AlertTriangle} label="High Risk (4-5)" value={fmtN(k.high_risk)} tone={k.high_risk > 0 ? "danger" : "default"} />
        <Tile icon={AlertTriangle} label="Poor Cond (4-5)" value={fmtN(k.poor_condition)} tone={k.poor_condition > 0 ? "warning" : "default"} />
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        {/* Renewal Horizon */}
        <Panel title="Renewal Horizon (by Replacement $)"
               subtitle="When each asset falls due based on install date + life expectancy">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.by_renewal_horizon || []} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="bucket" tick={{ fontSize: 10 }} tickFormatter={(v) => HORIZON_LABEL[v] || v} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={fmt$k} />
              <Tooltip formatter={(v, n) => n === "cost" ? fmt$(v) : fmtN(v)} labelFormatter={(v) => HORIZON_LABEL[v] || v} />
              <Bar dataKey="cost">
                {(data.by_renewal_horizon || []).map((row) => (
                  <Cell key={row.bucket} fill={HORIZON_COLORS[row.bucket] || "#94a3b8"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <MiniList items={data.by_renewal_horizon || []} renderRow={(r) => (
            <li key={r.bucket} className="flex items-center gap-2 text-[11px]">
              <span className="w-2 h-2 rounded-full" style={{ background: HORIZON_COLORS[r.bucket] }} />
              <span className="flex-1">{HORIZON_LABEL[r.bucket]}</span>
              <span className="text-slate-500 tabular-nums">{fmtN(r.count)} · {fmt$(r.cost)}</span>
            </li>
          )} />
        </Panel>

        {/* Condition Mix */}
        <Panel title="Condition Mix" subtitle="1 = Brand new · 5 = Needs replacement">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.by_condition || []} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="condition" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v, n) => n === "cost" ? fmt$(v) : fmtN(v)} />
              <Bar dataKey="count">
                {(data.by_condition || []).map((row) => (
                  <Cell key={row.condition} fill={COND_COLORS[row.condition] || "#94a3b8"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Panel>

        {/* Top Sites */}
        <Panel title="Top 10 Sites by Replacement Cost">
          <MiniBarTable items={(data.top_sites || []).map((r, i) => ({
            key: i,
            label: r.site || "(unspecified)",
            sub: r.client,
            count: r.count, cost: r.cost,
          }))} />
        </Panel>

        {/* Top Asset Types */}
        <Panel title="Top 10 Asset Types by Replacement Cost">
          <MiniBarTable items={(data.top_asset_types || []).map((r, i) => ({
            key: i,
            label: r.asset_type,
            count: r.count, cost: r.cost,
          }))} />
        </Panel>

        {/* Top Clients (only visible when no client filter is applied) */}
        {(data.top_clients || []).length > 1 && (
          <Panel title="Top 10 Clients by Replacement Cost" className="md:col-span-2">
            <MiniBarTable items={(data.top_clients || []).map((r, i) => ({
              key: i,
              label: r.client,
              count: r.count, cost: r.cost,
            }))} />
          </Panel>
        )}
      </div>
    </div>
  );
}


function Tile({ icon: Icon, label, value, tone }) {
  const cls = tone === "danger"  ? "border-rose-200 bg-rose-50/40 text-rose-700"
           : tone === "warning" ? "border-amber-200 bg-amber-50/40 text-amber-700"
           : tone === "info"    ? "border-sky-200 bg-sky-50/40 text-sky-700"
           : "border-slate-200 bg-white text-slate-800";
  return (
    <div className={`rounded-lg border ${cls} px-3 py-2 flex items-center gap-2`}>
      {Icon && <Icon className="w-4 h-4 opacity-60" />}
      <div className="flex-1 min-w-0">
        <div className="text-[9px] uppercase tracking-wider opacity-70">{label}</div>
        <div className="text-lg font-display font-semibold tabular-nums">{value}</div>
      </div>
    </div>
  );
}


function Panel({ title, subtitle, children, className = "" }) {
  return (
    <div className={`bg-white border border-slate-200 rounded-lg p-3 ${className}`}>
      <div className="mb-2">
        <div className="text-[11px] font-semibold text-slate-800">{title}</div>
        {subtitle && <div className="text-[10px] text-slate-500 mt-0.5">{subtitle}</div>}
      </div>
      {children}
    </div>
  );
}


function MiniList({ items, renderRow }) {
  return (
    <ul className="mt-2 space-y-0.5 max-h-[160px] overflow-y-auto">
      {items.map(renderRow)}
    </ul>
  );
}


function MiniBarTable({ items }) {
  const max = items.reduce((m, r) => Math.max(m, r.cost || 0), 0);
  return (
    <div className="space-y-1">
      {items.map((r) => {
        const pct = max > 0 ? (r.cost / max) * 100 : 0;
        return (
          <div key={r.key} className="text-[11px]">
            <div className="flex items-center gap-2">
              <span className="flex-1 truncate" title={r.label}>{r.label}</span>
              {r.sub && <span className="text-slate-400 text-[9px] truncate max-w-[80px]" title={r.sub}>{r.sub}</span>}
              <span className="tabular-nums text-slate-500">{fmtN(r.count)}</span>
              <span className="tabular-nums font-medium w-[70px] text-right">{fmt$k(r.cost)}</span>
            </div>
            <div className="h-1 bg-slate-100 rounded overflow-hidden mt-0.5">
              <div className="h-full bg-gbe" style={{ width: `${pct}%` }} />
            </div>
          </div>
        );
      })}
      {items.length === 0 && <div className="text-[11px] text-slate-400 italic">No data</div>}
    </div>
  );
}
